import { createSelector } from 'reselect'
import { convertStepToNlpModel, STABLE_EMPTY_ARRAY } from '@feedyou/utils'
import _ from 'lodash'
import {
  IntentHeadline,
  IntentListItem,
  IntentListItemId,
  NlpModel,
  NlpModelId,
  StepId,
  StepNlpConfiguration,
  KnowledgeBase
} from '@feedyou/schema'

import { RootState } from '../../reducers'
import { isHeadline, isIntent } from '../../features/nlpIntentList'
import { botFlowSelector } from '../flow/selectors/botFlowSelector'
import { getNlpModelById } from './utils'
import { stepTypeDefaultsSelector } from '../flow/selectors/stepTypeDefaultsSelector'
import { stepSelector } from '../flow/selectors/selectors'

export const nlpModelsSelector = createSelector(
  (s: RootState) => botFlowSelector(s)?.nlp?.models,
  (models): NlpModel[] => models ?? STABLE_EMPTY_ARRAY
)

export const knowledgeBasesSelector = createSelector(
  (state: RootState) => botFlowSelector(state)?.knowledgeBases,
  (knowledgeBases): KnowledgeBase[] => knowledgeBases ?? STABLE_EMPTY_ARRAY
)

export const knowledgeBaseByIdSelector = (knowledgeBaseId: string) => (state: RootState) => {
  return knowledgeBasesSelector(state).find(kb => kb.id === knowledgeBaseId)
}

export const stepNlpSettingsSelector = (s: RootState) => {
  return botFlowSelector(s)?.nlp?.stepNlp
}

export const stepNlpConfigsSelector = createSelector(
  (s: RootState) => stepNlpSettingsSelector(s)?.configurations,
  (configurations): StepNlpConfiguration[] => configurations ?? STABLE_EMPTY_ARRAY
)

export const nlpModelByIdSelector = (modelId: NlpModelId) => (state: RootState) => {
  return getNlpModelById(botFlowSelector(state), modelId)
}

export const intentListSelector =
  (modelId: NlpModelId) =>
  (state: RootState): IntentListItem[] | undefined => {
    return nlpModelByIdSelector(modelId)(state)?.intents
  }

export const intentsSelector =
  (modelId: NlpModelId) =>
  (state: RootState): IntentListItem[] | undefined => {
    return intentListSelector(modelId)(state)?.filter(isIntent)
  }

export const intentHeadlinesSelector =
  (modelId: NlpModelId) =>
  (state: RootState): IntentHeadline[] | undefined => {
    return intentListSelector(modelId)(state)?.filter(isHeadline)
  }

export const intentListItemSelector =
  (modelId: NlpModelId, itemId: IntentListItemId) => (state: RootState) => {
    const items = intentListSelector(modelId)(state)
    return items?.find(item => item.id === itemId)
  }

export const nlpIntentSelector =
  (modelId: NlpModelId, itemId: IntentListItemId) => (state: RootState) => {
    const intents = intentsSelector(modelId)(state)
    return intents?.find(intent => intent.id === itemId)
  }

export const nlpHeadlineSelector =
  (modelId: NlpModelId, itemId: IntentListItemId) => (state: RootState) => {
    const headlines = intentHeadlinesSelector(modelId)(state)
    return headlines?.find(headline => headline.id === itemId)
  }

export const nlpIntentDescriptionSelector =
  (modelId: NlpModelId, itemId: IntentListItemId) => (state: RootState) => {
    return nlpIntentSelector(modelId, itemId)(state)?.description
  }

export const stepNlpModelSelector = _.memoize((stepId: StepId) => {
  return createSelector(
    stepSelector(stepId),
    stepTypeDefaultsSelector,
    stepNlpSettingsSelector,
    (step, stepTypeDefaults, stepNlpSettings): NlpModel | undefined => {
      if (!step) {
        return undefined
      }

      return convertStepToNlpModel({ step, stepNlpSettings, stepTypeDefaults }) ?? undefined
    }
  )
})
